import { z } from "zod";
import {
  Box,
  CategoricalBarChart,
  Text,
  WidgetEmpty,
  WidgetSkeleton,
  compactNumber,
  useCategoricalColors,
  useCategoryRowBudget,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
  truncateLabel,
} from "@salestrak/ui";
import {
  useWindowEmptyTitle,
  useWindowPhrase,
} from "#app/widgets/dashboards/scope/use-window-copy";
import { useStageMoves, type StageMoveSlice } from "./use-stage-moves";
import { WidgetChartTooltip } from "../shared/widget-chart-tooltip";
import { resolveStoredTitle } from "../shared/derived-titles";
import type { WidgetSection } from "#app/widgets/dashboards/widget-sections";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";

export const stageMovesConfigSchema = z
  .looseObject({
    mode: z.enum(["byStage", "total"]).default("byStage"),
    limit: z.int().min(3).max(12).default(8),
    showGrid: z.boolean().default(true),
    showAxes: z.boolean().default(true),
  });

export type StageMovesConfig = z.infer<typeof stageMovesConfigSchema>;

/** Title mechanics: the per-mode derived titles are widget-generated and
 *  re-derive when the mode flips; renames stick. */
const MOVES_MODE_TITLE: Record<StageMovesConfig["mode"], string> = {
  byStage: "Moved into stage",
  total: "Total stage moves",
};
const GENERATED_MOVES_TITLES = new Set(Object.values(MOVES_MODE_TITLE));

function StageMovesRender({ h, config }: WidgetRenderContext<StageMovesConfig>) {
  const { total, slices, loading } = useStageMoves(config.mode, config.limit);
  const sliceColors = useCategoricalColors(slices);
  // The configured limit is a ceiling, not a promise: a short tile cannot name
  // twelve stages, and a chart handed more rows than it is tall stacks the
  // labels on top of each other. The rendered height decides how many survive.
  const { ref, budget } = useCategoryRowBudget();
  const emptyTitle = useWindowEmptyTitle("No stage moves");
  const windowPhrase = useWindowPhrase();

  if (loading) {
    return <WidgetSkeleton variant={config.mode === "total" ? "stat" : "bars"} />;
  }

  if (config.mode === "total") {
    return (
      <Box fill="flex" align="center" justify="center" paddingY="xs">
        <Box align="center" gap="2xs">
          <Text size={h >= 3 ? "4xl" : "2xl"} weight="bold" truncate={1}>
            {total.toLocaleString()}
          </Text>
          <Text size="sm" variant="muted" truncate={1}>
            {windowPhrase ? `stage moves ${windowPhrase}` : "stage moves"}
          </Text>
        </Box>
      </Box>
    );
  }

  if (slices.length === 0) {
    return (
      <WidgetEmpty title={emptyTitle} />
    );
  }

  const compact = h < 4;
  const showAxes = config.showAxes && !compact;
  const showGrid = config.showGrid && !compact;

  const bars = budget === null ? slices : slices.slice(0, budget);
  const hidden = slices.length - bars.length;

  return (
    <Box direction="column" fill="flex" gap="xs">
      <Box fill="flex" ref={ref}>
        <CategoricalBarChart<StageMoveSlice>
          data={bars}
          categoryKey="label"
          series={[{ key: "count" }]}
          orientation="rows"
          categoryColorTokens={sliceColors.tokens.slice(0, bars.length)}
          showGrid={showGrid}
          showAxes={showAxes}
          // The value axis drops out on a short card, but the category axis is
          // what NAMES each bar — without it the card is anonymous stripes. Only
          // the user's own Axes toggle turns it off.
          showCategoryAxis={config.showAxes}
          categoryTickFormatter={(v) => truncateLabel(v, 14)}
          valueTickFormatter={compactNumber}
          ariaLabel="Moves into stage"
          renderTooltip={(row) => (
            <WidgetChartTooltip
              label={row.label}
              details={`${row.count.toLocaleString()} ${row.count === 1 ? "move" : "moves"} into stage`}
            />
          )}
        />
      </Box>
      {hidden > 0 ? (
        <Text size="xs" variant="muted" truncate>
          {`+${hidden.toLocaleString()} more`}
        </Text>
      ) : null}
    </Box>
  );
}

const stageMovesSettings: WidgetSettingsGroup[] = [
  {
    title: "Data",
    fields: [
      {
        key: "mode",
        kind: "select",
        label: "Mode",
        icon: "bar-chart",
        options: [
          { value: "byStage", label: "By stage" },
          { value: "total", label: "Total" },
        ],
      },
      { key: "limit", kind: "number", label: "Max stages", icon: "list", min: 3, max: 12, step: 1 },
    ],
  },
  {
    title: "Display",
    fields: [
      { key: "showGrid", kind: "toggle", label: "Gridlines", icon: "grid-dots" },
      { key: "showAxes", kind: "toggle", label: "Axes", icon: "ruler" },
    ],
  },
];

export const stageMovesDef: WidgetModule<StageMovesConfig> = {
  type: "stage-moves",
  title: "Moved into stage",
  dynamicTitle: (config) =>
    resolveStoredTitle(
      config,
      (title) => GENERATED_MOVES_TITLES.has(title),
      MOVES_MODE_TITLE[config.mode],
    ),
  icon: "bar-chart",
  span: { minW: 3, minH: 3, maxW: 12, maxH: 12, defaultW: 5 },
  configSchema: stageMovesConfigSchema,
  settings: stageMovesSettings,
  catalog: [
    {
      key: "stage-moves.byStage",
      label: "Moved into stage",
      description: "Counts moves into each stage during the window, not Projects currently in that stage.",
      section: "stages" satisfies WidgetSection,
      tags: ["projects", "stages", "activity"] satisfies readonly WidgetTag[],
      // Label is in the generated family — bake nothing, let `dynamicTitle` track.
      autoTitle: false,
      defaultConfig: { mode: "byStage" },
      locks: ["mode"],
      defaultW: 5,
    },
  ],
  render: StageMovesRender,
};
